import { useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { track } from '@/lib/track'

/**
 * /auth — landing for OAuth callbacks.
 *
 * Convex Auth strips `?code=` from the URL as it processes the OAuth exchange.
 * We wait on the auth context's `isLoading` (which already covers both Convex
 * auth bootstrapping and the auth→profile gap) and then send the visitor home.
 * The homepage handles the rest — it already shows the right view for both
 * brand-new and returning users.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isLoading, isAuthenticated, user } = useAuth()
  const oauthError = searchParams.get('error')
  const tracked = useRef(false)

  useEffect(() => {
    if (oauthError) {
      navigate('/', { replace: true })
      return
    }
    if (isLoading) return
    if (isAuthenticated && user && !user.isAnonymous && !tracked.current) {
      tracked.current = true
      track('signin.completed', { method: 'google' })
    }
    // Auth settled. Whether the visitor ended up authenticated as a real user
    // or the exchange failed, the homepage is the safe destination.
    if (isAuthenticated && user && !user.isAnonymous) {
      navigate('/', { replace: true })
      return
    }
    if (!isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [oauthError, isLoading, isAuthenticated, user, navigate])

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Helmet>
        <title>Signing in… — Footage</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  )
}
